import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CreatePostModal = ({ onClose, onSubmit }) => {
  const [postType, setPostType] = useState('text');
  const [content, setContent] = useState('');
  const [workoutData, setWorkoutData] = useState({
    exercise: '',
    weight: '',
    reps: '',
    unit: 'lbs'
  });

  const postTypes = [
    { id: 'text', label: 'Text', icon: 'MessageSquare' },
    { id: 'workout', label: 'Workout', icon: 'Dumbbell' },
    { id: 'achievement', label: 'Achievement', icon: 'Award' },
    { id: 'milestone', label: 'Milestone', icon: 'Trophy' }
  ];

  const handleSubmit = () => {
    if (!content?.trim()) return;

    const postData = {
      post_type: postType,
      content: content?.trim()
    };

    if (postType === 'workout' || postType === 'achievement') {
      if (workoutData?.exercise) {
        postData.workout_data = {
          exercise: workoutData?.exercise,
          weight: parseFloat(workoutData?.weight) || null,
          reps: parseInt(workoutData?.reps) || null,
          unit: workoutData?.unit
        };
      }
    }

    onSubmit(postData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold text-foreground">Create Post</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Post Type Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">
              Post Type
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {postTypes?.map((type) => (
                <button
                  key={type?.id}
                  onClick={() => setPostType(type?.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-smooth ${
                    postType === type?.id
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                  }`}
                >
                  <Icon name={type?.icon} size={24} className={postType === type?.id ? 'text-primary' : 'text-muted-foreground'} />
                  <span className={`text-sm font-medium ${
                    postType === type?.id ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    {type?.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              What's on your mind?
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e?.target?.value)}
              placeholder="Share your fitness journey, achievements, or thoughts..."
              rows={6}
              className="w-full px-4 py-3 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground resize-none"
            />
          </div>

          {/* Workout Details (for workout/achievement posts) */}
          {(postType === 'workout' || postType === 'achievement') && (
            <div className="mb-6 p-4 bg-accent/50 rounded-lg">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Dumbbell" size={18} />
                Workout Details (Optional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Exercise
                  </label>
                  <input
                    type="text"
                    value={workoutData?.exercise}
                    onChange={(e) => setWorkoutData({ ...workoutData, exercise: e?.target?.value })}
                    placeholder="e.g., Deadlift, Bench Press"
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Weight
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={workoutData?.weight}
                      onChange={(e) => setWorkoutData({ ...workoutData, weight: e?.target?.value })}
                      placeholder="225"
                      className="flex-1 px-3 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm"
                    />
                    <select
                      value={workoutData?.unit}
                      onChange={(e) => setWorkoutData({ ...workoutData, unit: e?.target?.value })}
                      className="px-3 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm"
                    >
                      <option value="lbs">lbs</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Reps
                  </label>
                  <input
                    type="number"
                    value={workoutData?.reps}
                    onChange={(e) => setWorkoutData({ ...workoutData, reps: e?.target?.value })}
                    placeholder="5"
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!content?.trim()}
          >
            <Icon name="Send" size={18} className="mr-2" />
            Post
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreatePostModal;